import { FormState } from 'final-form';
import fileDownload from 'js-file-download';
import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { createEventTaskFactory } from '~admin/interactor/event/create-event';
import { Paths } from '~admin/route';
import { useCreateEditEventFlowStore } from '~admin/store/create-edit-event-flow-store-provider';
import { getCreateEventInitialValues } from '~admin/view/components/create-event/helper/create-event-initial-values.helper';
import { createEventDetailsValidators } from '~admin/view/components/create-event/helper/create-event-validators.helper';
import { EventDetails } from '~admin/view/components/details/event-details/event-details.component';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import { MarketplaceDetails } from '~admin/view/components/details/marketplace-details/marketplace-details.component';
import { OfferGroupDetails } from '~admin/view/components/details/offer-group-details/offer-group-details.component';
import { Wizard } from '~admin/view/components/ui/form/wizard.component';
import { Column } from '~common/components/column.component';
import { DataError } from '~common/components/core/data-error.component';
import { Title } from '~common/components/title.component';
import { useMutable } from '~common/hook/use-mutable';
import { useTask } from '~common/hook/use-task';
import { YesNoDialog } from '~web/view/components/layout/yes-no-dialog.component';

export const CreateEvent: React.FunctionComponent = observer(() => {
  const history = useHistory();
  const { t } = useTranslation();
  const createEventFlowStore = useCreateEditEventFlowStore();
  const createEventProfileTask = useTask(createEventTaskFactory);
  const { result, error } = createEventProfileTask;
  const [submittedFormData, setSubmittedFormData] = useMutable<EventFormData>(null);

  const onFormChange = (formState: FormState<EventFormData>): void => {
    createEventFlowStore.updateState(formState);
  };

  const onSubmit = (values: EventFormData): void => {
    createEventProfileTask(values);
    setSubmittedFormData(values);
  };

  const closeSaveDialogAndNavigate = (): void => {
    setSubmittedFormData(null);
    history.push(Paths.tool.events);
  };

  const onSaveToFile = (): void => {
    const formData = submittedFormData();
    fileDownload(JSON.stringify(formData), `${formData.eventName[0].value}.json`);
    closeSaveDialogAndNavigate();
  };

  return (
    <Container>
      <Title>{t('createEvent.headerText')}</Title>
      <Wizard
        onSubmit={onSubmit}
        submitButtonText={t('createEvent.createButton')}
        initialValues={submittedFormData() ?? getCreateEventInitialValues()}
        onFormChange={onFormChange}
        task={createEventProfileTask}
        allowImportJson
      >
        <EventDetails validate={createEventDetailsValidators} />
        <MarketplaceDetails />
        <OfferGroupDetails />
      </Wizard>
      {result && (
        <YesNoDialog
          title={t('createEvent.saveToFile')}
          onYes={onSaveToFile}
          onNo={closeSaveDialogAndNavigate}
        />
      )}
      {error && <DataError error={error} />}
    </Container>
  );
});

const Container = styled(Column)`
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  padding: 48px 35px 0;
  max-width: 950px;
`;



// WEBPACK FOOTER //
// ./view/components/create-event/create-event.component.tsx